import React from 'react';
import { Check, Copy } from 'lucide-react';

/** How long a button says "Copied" before it goes back to "Copy". */
export const COPIED_FOR_MS = 2000;

const motion = 'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]';
const focusRing = 'focus-visible:outline-none focus-visible:shadow-bark-focus';

/**
 * Copy one value, and fall back to selecting it.
 *
 * Copy writes `text` itself, never a rendering of it, so the clipboard holds
 * exactly the value shown. Where the browser refuses the clipboard (an
 * insecure context, a denied permission), the value is selected instead and
 * the block says so, so a keyboard copy still takes the exact value.
 */
function useCopyValue(text: string) {
    const valueRef = React.useRef<HTMLElement>(null);
    const resetTimer = React.useRef<number | undefined>(undefined);
    const [outcome, setOutcome] = React.useState<'idle' | 'copied' | 'failed'>('idle');

    React.useEffect(() => () => window.clearTimeout(resetTimer.current), []);

    const copy = async () => {
        window.clearTimeout(resetTimer.current);
        try {
            await navigator.clipboard.writeText(text);
            setOutcome('copied');
            resetTimer.current = window.setTimeout(() => setOutcome('idle'), COPIED_FOR_MS);
        } catch {
            const node = valueRef.current;
            const selection = window.getSelection();
            if (node && selection) selection.selectAllChildren(node);
            setOutcome('failed');
        }
    };

    return { valueRef, copied: outcome === 'copied', failed: outcome === 'failed', copy };
}

/** The live region and the refusal message both kinds of block share. */
function CopyFeedback({ copied, failed, label }: { copied: boolean; failed: boolean; label: string }) {
    return (
        <>
            <span role="status" aria-live="polite" className="sr-only">
                {copied ? `Copied ${label}` : ''}
            </span>
            {failed && (
                <p role="alert" className="mt-2 font-body text-[13px] leading-[1.5] text-danger">
                    Could not copy. The text is selected, so you can copy it with your keyboard.
                </p>
            )}
        </>
    );
}

interface CopyableTextProps {
    /** What the block shows and what Copy puts on the clipboard: the same string. */
    text: string;
    /** Completes the button's accessible name: "Copy <label>". */
    label: string;
    className?: string;
    /** One line, cut with an ellipsis (a long link). The full value is in `title`. */
    truncate?: boolean;
    /** `sunken` sits the field on a tinted ground, inside a white card. */
    tone?: 'surface' | 'sunken';
    /** The word on the button. Its accessible name is always "Copy <label>"; the
     *  live region, not the name, announces "Copied <label>". */
    buttonText?: string;
    /** Use regular body text for forwardable instructions; commands stay monospace. */
    textStyle?: 'code' | 'prose';
}

/**
 * One value to paste somewhere else (a URL, a command), shown as code in a
 * field with a Copy button. On a phone the button sits under the value, so the
 * value keeps the full width.
 */
export default function CopyableText({
    text,
    label,
    className = '',
    truncate = false,
    tone = 'surface',
    buttonText = 'Copy',
    textStyle = 'code',
}: CopyableTextProps) {
    const { valueRef, copied, failed, copy } = useCopyValue(text);
    return (
        // data-copyable / data-copy-value: hooks for the tests, which read the
        // value a button copies. Not an API.
        <div className={className} data-copyable="">
            <div
                className={`flex flex-col items-stretch gap-3 rounded-xl border border-border-default p-3 sm:flex-row sm:items-center sm:py-2 sm:pl-4 sm:pr-2 ${
                    tone === 'sunken' ? 'bg-surface-sunken' : 'bg-surface'
                }`}
            >
                <code
                    ref={valueRef}
                    data-copy-value=""
                    title={truncate ? text : undefined}
                    className={`min-w-0 flex-1 leading-[1.5] ${textStyle === 'prose' ? 'font-body text-[15px]' : 'font-mono text-[13px] font-medium'} ${
                        truncate ? 'truncate text-fg-secondary' : 'whitespace-pre-wrap break-words text-fg'
                    }`}
                >
                    {text}
                </code>
                <button
                    type="button"
                    onClick={() => void copy()}
                    aria-label={`Copy ${label}`}
                    className={`inline-flex h-9 shrink-0 items-center justify-center rounded-lg border px-3.5 font-body text-[13px] font-semibold active:translate-y-px ${motion} ${focusRing} ${
                        copied
                            ? 'border-success bg-success-bg text-success'
                            : 'border-border-strong bg-surface text-fg hover:border-fg-tertiary'
                    }`}
                >
                    <span aria-hidden="true">{copied ? 'Copied ✓' : buttonText}</span>
                </button>
            </div>
            <CopyFeedback copied={copied} failed={failed} label={label} />
        </div>
    );
}

interface CopyablePromptProps {
    /** The question: what the card shows inside its quote marks, and what Copy takes. */
    text: string;
    /** Completes the button's accessible name: "Copy <label>". */
    label: string;
    className?: string;
}

/**
 * A question to paste into an assistant: one list row with an accent bar and
 * an icon-only Copy button that turns into a check while "Copied" shows.
 */
export function CopyablePrompt({ text, label, className = '' }: CopyablePromptProps) {
    const { valueRef, copied, failed, copy } = useCopyValue(text);
    const Icon = copied ? Check : Copy;

    return (
        <div className={className} data-copyable="">
            <div className="flex items-center gap-4 border-b border-divider py-4 pl-5 pr-3">
                <span aria-hidden="true" className="w-[3px] self-stretch rounded-sm bg-primary" />
                <p className="min-w-0 flex-1 font-body text-[15px] font-medium leading-[1.5] text-fg">
                    <span ref={valueRef} data-copy-value="">
                        {text}
                    </span>
                </p>
                <button
                    type="button"
                    onClick={() => void copy()}
                    aria-label={`Copy ${label}`}
                    className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border active:translate-y-px ${motion} ${focusRing} ${
                        copied
                            ? 'border-success bg-success-bg text-success'
                            : 'border-border-default bg-surface text-fg-secondary hover:border-fg-tertiary hover:text-fg'
                    }`}
                >
                    <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                </button>
            </div>
            <CopyFeedback copied={copied} failed={failed} label={label} />
        </div>
    );
}
